import React, { useId } from "react";

import { useTranslation } from "react-i18next";

import { SwitchFieldBase } from "@/src/components/form-fields/SwitchField";
import { FieldTitle } from "@/src/components/ui/field";
import { ToggleGroup, ToggleGroupItem } from "@/src/components/ui/toggle-group";
import { cn } from "@/src/lib/utils";

import { IMAGE_FORMATS } from "../../../constants/ratio";

import type { JSX } from "react";

import type { ImageFormat } from "../../../constants/ratio";

interface IImageFormatSelector {
  title?: string;
  selected?: ImageFormat;
  onChangeRatio: (ratio: ImageFormat) => void;
  imageFormatList: ImageFormat[];
  isDisabled?: boolean;
  hasDisableSwitch?: boolean;
  onChangeDisabledImage?: (disabled: boolean) => void;
  className?: string;
}

const SHAPE_CLASS_BY_FORMAT: Record<ImageFormat, string> = {
  [IMAGE_FORMATS.FORMAT_16_6]: "h-[21px] w-14",
  [IMAGE_FORMATS.FORMAT_16_9]: "h-8 w-14",
  [IMAGE_FORMATS.FORMAT_3_4]: "h-8 w-6",
  [IMAGE_FORMATS.FORMAT_1_1]: "size-8",
  [IMAGE_FORMATS.FORMAT_1_1_ROUND]: "size-8 rounded-full",
  [IMAGE_FORMATS.FORMAT_2_3]: "h-8 w-[21px]",
  [IMAGE_FORMATS.FORMAT_16_3]: "h-[11px] w-14",
};

function isImageFormat(value: string): value is ImageFormat {
  return value in SHAPE_CLASS_BY_FORMAT;
}

function ImageFormatSelector({
  title,
  selected,
  onChangeRatio,
  imageFormatList,
  isDisabled,
  hasDisableSwitch,
  onChangeDisabledImage,
  className,
}: IImageFormatSelector): JSX.Element {
  const { t } = useTranslation("editor-platform");
  const titleId = useId();

  const handleValueChange = (value: string): void => {
    if (isImageFormat(value)) onChangeRatio(value);
  };

  return (
    <div className={cn("flex flex-col gap-1.25 pb-2.5", className)}>
      {hasDisableSwitch ? (
        <div className="mb-1.25">
          <SwitchFieldBase
            checked={!!isDisabled}
            label={t("hideImage")}
            variant="inline"
            onCheckedChange={(checked: boolean): void =>
              onChangeDisabledImage?.(checked)
            }
          />
        </div>
      ) : null}
      {title ? <FieldTitle id={titleId}>{title}</FieldTitle> : null}
      <ToggleGroup
        aria-labelledby={title ? titleId : undefined}
        className="flex-wrap gap-2.5"
        disabled={isDisabled}
        onValueChange={handleValueChange}
        type="single"
        value={selected ?? ""}
      >
        {imageFormatList.map((imageFormat) => {
          const isActive = imageFormat === selected && !isDisabled;
          return (
            <ToggleGroupItem
              key={imageFormat}
              value={imageFormat}
              className={cn(
                "border-border bg-muted h-19 w-22 flex-col justify-center gap-2 rounded-lg border-2 px-0",
                "data-[state=on]:border-primary data-[state=on]:bg-primary/10",
                "disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              <span className="flex h-8 w-14 items-center justify-center">
                <span
                  aria-hidden="true"
                  className={cn(
                    "bg-input rounded-xs",
                    SHAPE_CLASS_BY_FORMAT[imageFormat],
                    isActive && "bg-primary",
                  )}
                />
              </span>
              <span
                className={cn(
                  "text-muted-foreground text-xs font-semibold",
                  isActive && "text-primary-700 dark:text-primary",
                )}
              >
                {t(imageFormat)}
              </span>
            </ToggleGroupItem>
          );
        })}
      </ToggleGroup>
    </div>
  );
}

export default ImageFormatSelector;
